import React, { useState, useEffect } from 'react';
import { Button, Modal } from '@jjj/components';
import { connect } from 'react-redux';
import { ModalBodyWrapper } from './addApplicantModal.style';
import { t } from '../../../../../../i18n';
import { ChooseTalent } from './steps/chooseTalent/chooseTalent';
import { ChoosePosition } from './steps/choosePosition/choosePosition';
import actions from './actions';

const STEPS = {
  CHOOSE_TALENT: 0,
  CHOOSE_POSITION: 1,
};

export const TYPES = {
  UNIT: 'unit',
  SUBUNIT: 'subunit',
};

const AddApplicantModal = props => {
  const {
    data,
    changeProperty,
    clearStore,
    loadTalentsRequest,
    companyId,
    addTalentToMyTeamRequest,
    closeModal,
    type = TYPES.UNIT,
  } = props;
  const [step, setStep] = useState(STEPS.CHOOSE_TALENT);
  const [filter, setFilter] = useState('');
  const { editForm, loading, talents, totalCount, isModalVisible } = data;

  const selectedTalent = editForm.talent.value;
  const selectedPositions = editForm.positions.value;

  useEffect(() => {
    if (isModalVisible && !talents && !loading) {
      loadTalentsRequest({ id: companyId, type });
    }
  }, [isModalVisible, talents, loading, loadTalentsRequest, companyId, type]);

  useEffect(() => {
    if ((!selectedTalent, !selectedPositions)) {
      setStep(STEPS.CHOOSE_TALENT);
    }
  }, [selectedTalent, selectedPositions]);

  const getAllSelectedOptions = () => {
    const positions = [];

    if (selectedPositions) {
      Object.values(selectedPositions).forEach(array => {
        positions.push(...array);
      });
    }

    return positions;
  };

  const onFilterChange = value => {
    setFilter(value);
  };

  const clearFilter = () => {
    setFilter('');
  };

  const handleNext = () => {
    if (step === STEPS.CHOOSE_TALENT) {
      setStep(STEPS.CHOOSE_POSITION);
    } else {
      const requestData = {
        type,
        id: companyId,
        data: {
          company_team_member: {
            worker_id: selectedTalent,
            position_ids: getAllSelectedOptions(),
          },
        },
      };

      addTalentToMyTeamRequest(requestData);
    }
  };

  const handlePropertyChange = ({ attr, value }) => {
    changeProperty({ attr, value });
  };

  const handleOnClose = () => {
    clearStore();
    closeModal();
    clearFilter();
  };

  const isNextButtonDisabled = () => {
    switch (step) {
      case STEPS.CHOOSE_TALENT:
        return !selectedTalent;

      case STEPS.CHOOSE_POSITION:
        return !getAllSelectedOptions()?.length;
      default:
        return true;
    }
  };

  const onLoadData = params => {
    loadTalentsRequest({
      ...params,
      id: companyId,
      type,
    });
  };

  const getSelectedTalentData = talentId => talents?.find(talent => talent.id === talentId);

  return (
    <Modal
      wrapClassName="custom-footer-alignment"
      title={t('dash.units.unit.my_team.add_talent_modal.header')}
      open={isModalVisible}
      destroyOnHidden
      width={600}
      onCancel={handleOnClose}
      centered
      footer={[
        <Button
          key="cancel"
          type="secondary"
          className="cancel-btn"
          onClick={handleOnClose}
          data-cy="client-unit-my-team-add-applicant-modal-cancel-button"
        >
          {t('dash.common.button.cancel_label')}
        </Button>,
        <Button
          key="next"
          type="primary"
          className="next-btn"
          onClick={handleNext}
          disabled={isNextButtonDisabled()}
          data-cy="client-unit-my-team-add-applicant-modal-next-button"
        >
          {t('dash.common.button.next_label')}
        </Button>,
      ]}
    >
      <ModalBodyWrapper>
        <>
          {step === STEPS.CHOOSE_TALENT && (
            <ChooseTalent
              talents={talents || []}
              loading={loading}
              selectedTalent={selectedTalent}
              onChange={handlePropertyChange}
              loadData={onLoadData}
              totalCount={totalCount}
              filter={filter}
              onFilterChange={onFilterChange}
            />
          )}

          {step === STEPS.CHOOSE_POSITION && (
            <ChoosePosition
              selectedPositions={selectedPositions}
              onChange={handlePropertyChange}
              selectedTalent={getSelectedTalentData(selectedTalent)}
            />
          )}
        </>
      </ModalBodyWrapper>
    </Modal>
  );
};

export default connect(
  state => ({ data: state.AddApplicantToMyTeam }),
  dispatch => ({
    changeProperty: /* istanbul ignore next */ data => dispatch(actions.changeProperty(data)),
    loadTalentsRequest: /* istanbul ignore next */ data => dispatch(actions.loadTalentsRequest(data)),
    addTalentToMyTeamRequest: /* istanbul ignore next */ data => dispatch(actions.addTalentToMyTeamRequest(data)),
    showModal: /* istanbul ignore next */ () => dispatch(actions.showModal()),
    closeModal: /* istanbul ignore next */ () => dispatch(actions.closeModal()),
    clearStore: /* istanbul ignore next */ () => dispatch(actions.clearStore()),
  }),
)(AddApplicantModal);
